import { useState } from 'react'
import { useGameStore } from '@store/gameStore'
import { GameLayout } from '@components/GameLayout'
import './SetupScreen.css'

export function SetupScreen() {
    const [step, setStep] = useState<'welcome' | 'name' | 'hero' | 'empire'>('welcome')
    const [customName, setCustomName] = useState('')
    const [selectedHero, setSelectedHero] = useState('default')
    const [selectedColor, setSelectedColor] = useState('#3b82f6')
    const [selectedEmblem, setSelectedEmblem] = useState('🏰')
    const [isProcessing, setIsProcessing] = useState(false)

    // Store
    const setCustomization = useGameStore(state => state.setCustomization)
    const setScreen = useGameStore(state => state.setScreen)
    const trackMoneySpent = useGameStore(state => state.trackMoneySpent)
    const username = useGameStore(state => state.username)

    // Configuration
    const HERO_SKINS = [
        { id: 'default', name: 'Rookie', price: 0, image: './rookie.png' },
        { id: 'knight', name: 'Ritter', price: 9.99, image: './ritter.png' },
        { id: 'king', name: 'König', price: 9.99, image: './king.png' },
        { id: 'wizard', name: 'Magier', price: 9.99, image: './magier.png' },
    ]

    const EMPIRE_COLORS = [
        { id: 'blue', value: '#3b82f6', price: 0 },
        { id: 'red', value: '#ef4444', price: 4.99 },
        { id: 'green', value: '#10b981', price: 4.99 },
        { id: 'purple', value: '#8b5cf6', price: 4.99 },
        { id: 'gold', value: '#f59e0b', price: 4.99 },
    ]

    const EMPIRE_EMBLEMS = [
        { id: 'castle', value: '🏰', price: 0 },
        { id: 'sword', value: '⚔️', price: 4.99 },
        { id: 'dragon', value: '🐉', price: 4.99 },
        { id: 'crown', value: '👑', price: 4.99 },
        { id: 'skull', value: '💀', price: 4.99 },
    ]

    const handlePayment = (amount: number, callback: () => void) => {
        setIsProcessing(true)
        setTimeout(() => {
            setIsProcessing(false)
            trackMoneySpent(amount)
            callback()
        }, 1500)
    }

    const handleNameSubmit = () => {
        if (customName.trim()) {
            handlePayment(1.99, () => {
                setCustomization({ username: customName.trim() })
                setStep('hero')
            })
        } else {
            setStep('hero')
        }
    }

    const handleHeroSelect = (skinId: string) => {
        setSelectedHero(skinId)
    }

    const handleHeroSubmit = () => {
        const selectedSkin = HERO_SKINS.find(s => s.id === selectedHero)
        if (selectedSkin && selectedSkin.price > 0) {
            handlePayment(selectedSkin.price, () => setStep('empire'))
        } else {
            setStep('empire')
        }
    }

    const handleEmpireSelect = (color: string, emblem: string) => {
        setSelectedColor(color)
        setSelectedEmblem(emblem)
    }

    const finishSetup = () => {
        const colorPrice = EMPIRE_COLORS.find(c => c.value === selectedColor)?.price || 0
        const emblemPrice = EMPIRE_EMBLEMS.find(e => e.value === selectedEmblem)?.price || 0
        const totalPrice = colorPrice + emblemPrice

        const finalize = () => {
            setCustomization({
                heroSkin: selectedHero,
                empireColor: selectedColor,
                empireEmblem: selectedEmblem
            })
            setScreen('loading')
        }

        if (totalPrice > 0) {
            handlePayment(totalPrice, finalize)
        } else {
            finalize()
        }
    }

    // RENDER HELPERS
    const renderWelcome = () => (
        <div className="setup__step setup__step--welcome">
            <img src="./logo.png" alt="Pay2Lose Logo" className="setup__logo" />
            <h1 className="setup__title">Herzlich Willkommen</h1>
            <p className="setup__desc">
                Bevor du dein Imperium gründest, müssen wir ein paar... "Formalitäten" klären.
            </p>
            <button className="setup__btn setup__btn--premium" onClick={() => setStep('name')}>
                Imperium gründen
            </button>
        </div>
    )

    const renderName = () => (
        <div className="setup__step">
            <h2 className="setup__subtitle">Wer bist du?</h2>

            <div className="setup__option-card">
                <span className="setup__badge">GRATIS</span>
                <h3 className="setup__option-title">{username}</h3>
                <p className="setup__option-desc">Eine generische ID für einen generischen Spieler.</p>
                <button className="setup__btn setup__btn--secondary" onClick={() => setStep('hero')}>
                    Auswählen
                </button>
            </div>

            <div className="setup__divider">ODER</div>

            <div className="setup__option-card setup__option-card--premium">
                <span className="setup__badge setup__badge--premium">1,99 €</span>
                <h3 className="setup__option-title">Eigener Name</h3>
                <input
                    type="text"
                    className="setup__input"
                    placeholder="Dein Name..."
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                />
                <button
                    className="setup__btn setup__btn--premium"
                    onClick={handleNameSubmit}
                    disabled={!customName.trim()}
                >
                    Kaufen & Wählen
                </button>
            </div>
        </div>
    )

    const renderHero = () => (
        <div className="setup__step">
            <h2 className="setup__subtitle">Wähle deinen Helden</h2>
            <div className="setup__grid">
                {HERO_SKINS.map(skin => (
                    <div
                        key={skin.id}
                        className={`setup__hero-card ${selectedHero === skin.id ? 'setup__hero-card--active' : ''}`}
                        onClick={() => handleHeroSelect(skin.id)}
                    >
                        {/* Background Image */}
                        <img src={skin.image} alt={skin.name} className="setup__hero-bg" />

                        {/* Top Right Price/Badge */}
                        <div className={`setup__hero-badge ${skin.price > 0 ? 'premium' : 'free'}`}>
                            {skin.price > 0 ? `${skin.price}€` : 'GRATIS'}
                        </div>

                        {/* Footer Name */}
                        <div className="setup__hero-footer">
                            <span className="setup__hero-name">{skin.name}</span>
                        </div>

                        {/* Selected Indicator Overlay */}
                        {selectedHero === skin.id && <div className="setup__hero-overlay"></div>}
                        {selectedHero === skin.id && <div className="setup__check">✓</div>}
                    </div>
                ))}
            </div>
            <button className="setup__btn setup__btn--premium" onClick={handleHeroSubmit}>
                Weiter
            </button>
        </div>
    )

    const renderEmpire = () => (
        <div className="setup__step">
            <h2 className="setup__subtitle">Gestalte dein Reich</h2>

            {/* Preview */}
            <div className="setup__preview" style={{ '--preview-color': selectedColor } as React.CSSProperties}>
                <div className="setup__preview-flag">
                    <span className="setup__preview-emblem">{selectedEmblem}</span>
                </div>
            </div>

            <div className="setup__section">
                <h3>Farbe wählen</h3>
                <div className="setup__colors">
                    {EMPIRE_COLORS.map(color => (
                        <div
                            key={color.id}
                            className={`setup__color-btn ${selectedColor === color.value ? 'active' : ''}`}
                            style={{ background: color.value }}
                            onClick={() => handleEmpireSelect(color.value, selectedEmblem)}
                        >
                            {color.price > 0 && <span className="setup__price-tag">{color.price}€</span>}
                        </div>
                    ))}
                </div>
            </div>

            <div className="setup__section">
                <h3>Wappen wählen</h3>
                <div className="setup__emblems">
                    {EMPIRE_EMBLEMS.map(emblem => (
                        <div
                            key={emblem.id}
                            className={`setup__emblem-btn ${selectedEmblem === emblem.value ? 'active' : ''}`}
                            onClick={() => handleEmpireSelect(selectedColor, emblem.value)}
                        >
                            {emblem.value}
                            {emblem.price > 0 && <span className="setup__price-tag">{emblem.price}€</span>}
                        </div>
                    ))}
                </div>
            </div>

            <button className="setup__btn setup__btn--premium" onClick={finishSetup}>
                {(
                    (EMPIRE_COLORS.find(c => c.value === selectedColor)?.price || 0) +
                    (EMPIRE_EMBLEMS.find(e => e.value === selectedEmblem)?.price || 0)
                ) > 0 ? `Kaufen & Starten (${(
                    (EMPIRE_COLORS.find(c => c.value === selectedColor)?.price || 0) +
                    (EMPIRE_EMBLEMS.find(e => e.value === selectedEmblem)?.price || 0)
                ).toFixed(2)} €)` : 'Fertigstellen'}
            </button>
        </div>
    )

    return (
        <GameLayout showNav={false} showHeader={false} noPadding={true}>
            <div className="setup">
                {step !== 'welcome' && (
                    <img src="./logo.png" alt="Logo" className="setup__logo-small" />
                )}

                {step === 'welcome' && renderWelcome()}
                {step === 'name' && renderName()}
                {step === 'hero' && renderHero()}
                {step === 'empire' && renderEmpire()}

                {isProcessing && (
                    <div className="setup__processing">
                        <div className="setup__spinner">💸</div>
                        <span>Zahlung wird verarbeitet...</span>
                    </div>
                )}
            </div>
        </GameLayout>
    )
}
